import { nanoid } from "nanoid";
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Switch from 'react-switch';
import { api, baseURL, superAdminReqConfig } from "../../include/api";
import { getSuperAdmin } from "../../include/function";
import Error from "../add-ons/Error";
import Spinner from "../add-ons/Spinner";
import SuperAdminHeader from "./Header";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';


const SuperAdminProduct = () => {
    const user = getSuperAdmin();

    const [fetchingProducts, setFetchingProducts] = useState(true);
    const [productList, setProductList] = useState([]);
    const [activeTab, setActiveTab] = useState('active');
    const [loadingStatusChange, setLoadingStatusChange] = useState(false);

    const [searchTerm, setSearchTerm] = useState(""); // Search term state

    const getProducts = useCallback(() => {
        setFetchingProducts(true);
        api.get('/product/', superAdminReqConfig()).then(response => {
            if (response.status === 200) {
                setProductList(response.data.data);
            }
        }).catch(error => {
            setProductList([]);
            console.error("Error fetching products:", error);
        }).finally(() => setFetchingProducts(false));
    }, []);

    useEffect(() => {
        getProducts();
    }, [getProducts]);

    // Handles the status change of the product
    const handleStatusChange = (productId, newStatus) => {
        setLoadingStatusChange(true);
        api.put(`/product_status/?id=${productId}`, { status: newStatus }, superAdminReqConfig())
            .then(response => {
                if (response.status === 200) {
                    getProducts();
                }
            })
            .catch(error => {
                console.error("Error updating product status:", error);
            })
            .finally(() => setLoadingStatusChange(false));
    };

    // Handles the deletion of the product
    const handleDeleteProduct = (productId) => {
        if (window.confirm("Are you sure you want to delete this product?")) {
            api.delete(`/product/?id=${productId}`, superAdminReqConfig())
                .then(response => {
                    if (response.status === 200) {
                        getProducts();
                    }
                })
                .catch(error => {
                    console.error("Error deleting product:", error);
                });
        }
    };

    // Filter products based on the selected tab (active/inactive/not approved)
        const filteredProducts = (productList || [])
  .filter(product => product.status === activeTab)
  .filter(product => product.name.toLowerCase().includes(searchTerm.toLowerCase()));

    return (
        <>

            <SuperAdminHeader>

                <div className="fixed-top-bar" style={{ marginBottom: "10%" }}>
                    <div className="container my-1 mx-1">
                        <div className="py-3 d-flex justify-content-between align-items-center">
                            <h5 className="m-0">Product</h5>
                            <Link to="/super/add-product" className="btn btn-success">Add new product</Link>
                        </div>

                        <div className="mb-3">
                            <ul className="nav nav-tabs" id="myTab" role="tablist">
                                {['active', 'inactive', 'not approved'].map(tab => (
                                    <li className="nav-item" role="tab" key={tab}>
                                        <button
                                            className={`nav-link ${activeTab === tab ? 'active' : ''}`}
                                            onClick={() => setActiveTab(tab)}
                                        >
                                            {tab.charAt(0).toUpperCase() + tab.slice(1)}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div className="mb-3 d-flex justify-content-between align-items-center">
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Search products by name..."
                                value={searchTerm} // Bind the search input to state
                                onChange={(e) => setSearchTerm(e.target.value)} // Update state on user input
                            />
                            <br/>
                            </div>
                            <div className="mb-3">
                              <span className="text-dark text-align-center small blink">
                                Total of {filteredProducts.length} product{filteredProducts.length !== 1 ? 's' : ''}
                            </span>
                        </div>

                        {fetchingProducts ? (
    <Spinner color="danger" spinnerSize="lg" />
) : (
    filteredProducts.length > 0 ? (
        <div className="table-responsive">
            <table className="table table-bordered align-middle">
                <thead className="table-dark">
                    <tr>
                        <th>Images</th>
                        <th>Name</th>
                        <th>Ratings</th>
                        <th>Price</th>
                        <th>Shop Name</th>
                        <th>Delivery Charge</th>
                        <th>Offers</th>
                        <th>Warranty</th>
                        <th>Status</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {filteredProducts.map((product) => (
                        <tr key={product.id}>
                            <td>
                                <img src={`${baseURL}/${product.mainImage}`} alt={product.name} style={{ width: 180, height: 130, objectFit: 'contain' }} />
                            </td>
                            <td>
                                <span className="fw-bold">{product.name}</span>
                            </td>
                            <td>
                                <span className="badge bg-success">{product.rating} &#9733;</span>
                            </td>
                            <td>
                                <span className="fw-bold">&#8377;{product.price}</span>
                            </td>
                            <td>
                                {(product.shopName && product.shopName !== '') ? product.shopName : 'No Shop Name'}
                            </td>
                            <td>
                                {(product.deliveryCharge && product.deliveryCharge !== 0) ? <>&#8377;{product.deliveryCharge}</> : 'FREE'}
                            </td>
                            <td>
                                {product.discountValue !== 0 && product.discountLabel ? product.discountLabel : 'No Offer'}
                            </td>
                            <td>
                                {product.warrantyPeriod !== 0 && product.warrantyLabel ? product.warrantyLabel : 'No Warranty'}
                            </td>
                            <td>
                                {product.status === 'active' && <span className="badge bg-success blink">Active</span>}
                                {product.status === 'inactive' && <span className="badge bg-danger blink">Inactive</span>}
                                {product.status === 'not approved' && <span className="badge bg-warning text-dark blink">Not Approved</span>}
                            </td>
                            <td className="text-center">
                                <div className="d-flex flex-column align-items-center gap-1">
                                    <Link to={`/admin/edit-product/${product.id}`} className="btn btn-primary">Edit</Link>
                                    {product.status === 'not approved' ? (
                                        <button
                                            className="btn btn-sm btn-success mb-1"
                                            onClick={() => handleStatusChange(product.id, 'active')}
                                            disabled={loadingStatusChange}
                                        >
                                            Approve
                                        </button>
                                    ) : (
                                        <div className="d-flex justify-content-center align-items-center">
                                            <Switch
                                                checked={product.status === 'active'}
                                                onChange={() => handleStatusChange(product.id, product.status === 'active' ? 'inactive' : 'active')}
                                                disabled={loadingStatusChange}
                                                onColor="#4caf50"
                                                offColor="#f44336"
                                                handleDiameter={22}
                                                height={24}
                                                width={55}
                                            />
                                        </div>
                                    )}
                                    {/* Uncomment to enable delete
                                    <button
                                        className="btn btn-sm btn-outline-danger"
                                        onClick={() => handleDeleteProduct(product.id)}
                                    >
                                        Delete
                                    </button>
                                    */}
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    ) : (
        <Error
            mainText="No product found!"
            secondaryText={<Link to="/super/add-product">Add New product</Link>}
        />
    )
)}                    </div>
                </div>
            </SuperAdminHeader>
        </>
    );
};

const Product = ({ product, canEditProduct, handleStatusChange, handleDeleteProduct, loadingStatusChange }) => {
    const getNextStatus = (currentStatus) => {
        switch (currentStatus) {
            case 'not approved':
                return 'active';
            case 'active':
                return 'inactive';
            case 'inactive':
                return 'active';
            default:
                return currentStatus;
        }
    };

    const isActive = product.status === 'active';
    const isNotApproved = product.status === 'not approved';

    return (
        <div className="col-12 col-sm-6 col-lg-4 p-2">
            <div className="border shadow-sm w-100 h-100 d-block">
                <div className="img-nor w-100 p-3 border-bottom d-flex justify-content-center">
                    <img src={`${baseURL}/${product.mainImage}`} alt={product.name} className="fit-contain w-100 h-100" />
                </div>
                <div className="p-2">
                    <h6 className="d-flex align-items-center justify-content-between fw-bold">
                        <div className="text-truncate">
                            <i className={`fas fa-circle blink ${isActive ? 'text-success' : product.status === 'inactive' ? 'text-danger' : 'text-warning'}`} style={{ fontSize: '.7rem' }} />
                            <span className="ps-2">{product.name}</span>
                        </div>
                        {canEditProduct && (
                            <div className="d-flex align-items-center">
                                <Link to={`/admin/edit-product/${product.id}`} className="small d-flex align-items-center hover-underline">Edit <i className="fas fa-pencil-alt fa-sm ps-2" /></Link>
                                {/* <button className="btn btn-danger btn-sm ms-2" onClick={() => handleDeleteProduct(product.id)}>
                                    <FontAwesomeIcon icon={faTrash} />
                                </button> */}
                                
                            </div>
                        )}
                    </h6>
                    <div className="mb-1 text-truncate ps-2">
                        <span className="badge bg-success">{product.rating} &#9733;</span>
                        <span className="small text-muted fw-bold ms-3">&#8377;{product.price}</span>
                    </div>
                    <ul className="small text-muted ps-2 m-0">
                        <li className="text-truncate">
                            Shop name: {(product.shopName && product.shopName !== '') ? product.shopName : 'No Shop Name'}
                        </li>
                        <li className="text-truncate">
                            Delivery Charge: {(product.deliveryCharge && product.deliveryCharge !== 0) ? <>&#8377;{product.deliveryCharge}</> : 'FREE'}
                        </li>
                        <li className="text-truncate">
                            Offer: {product.discountValue !== 0 && product.discountLabel ? product.discountLabel : 'No Offer'}
                        </li>
                        <li className="text-truncate">
                            Warranty: {product.warrantyPeriod !== 0 && product.warrantyLabel ? product.warrantyLabel : 'No Warranty'}
                        </li>
                    </ul>
                </div>
                <div className="p-2 hide-scrollbar" style={{ overflowX: 'auto', whiteSpace: 'nowrap' }}>
                    {product.images && product.images.length > 0 && product.images.map(image => (
                        <ProductImage
                            src={`${baseURL}/${image.imageUrl}`}
                            key={nanoid()}
                        />
                    ))}
                </div>
                <div className="p-2 d-flex justify-content-center align-items-center">
                    {isNotApproved ? (
                        <button
                            className="btn btn-success"
                            onClick={() => handleStatusChange(product.id, getNextStatus(product.status))}
                            disabled={loadingStatusChange} // Disable during loading
                        >
                            Approve
                        </button>
                    ) : (
                        <>
                            <Switch
                                checked={isActive}
                                onChange={() => handleStatusChange(product.id, getNextStatus(product.status))}
                                disabled={loadingStatusChange} // Disable during loading
                                onColor="#4caf50"
                                offColor="#f44336"
                                onHandleColor="#ffffff"
                                offHandleColor="#ffffff"
                                checkedIcon={<div style={checkedIconStyle}></div>}
                                uncheckedIcon={<div style={uncheckedIconStyle}></div>}
                                handleDiameter={22}
                                height={28}
                                width={48}
                                boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
                                activeBoxShadow="0px 0px 1px 10px rgba(0, 0, 0, 0.2)"
                            />
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

const checkedIconStyle = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    fontSize: 12,
    color: 'white',
    paddingRight: 2,
    paddingLeft: 2,
    transition: 'transform 0.3s ease',
    transform: 'translateX(-2px)' // Position for active state
};

const uncheckedIconStyle = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    fontSize: 12,
    color: 'white',
    paddingRight: 2,
    paddingLeft: 2,
    transition: 'transform 0.3s ease',
    transform: 'translateX(2px)' // Position for inactive state
};

const ProductImage = ({ src }) => {
    return (
        <div className="form-control me-2 p-0 d-inline-block" style={{ width: '60px', height: '60px' }}>
            <img src={src} alt="" className="fit-contain w-100 h-100" />
        </div>
    );
};

export default SuperAdminProduct;